import React, {useState} from 'react'
import classNames from 'classnames'
import AdyenPaymentForm from '../common/AdyenPaymentForm'
import {
  useGetPaymentMethodsQuery,
  useGetPaymentSessionQuery
} from '../../services/residentPortalApi'
import {
  filterAllowedPaymentMethods,
  formatAmount,
  hasApplicationStatus,
  isNumeric,
  parseNumber
} from '../../services/utils'
import {
  LeaseApplication,
  LeaseApplicationStatus,
  LeaseApplicationType,
  PaymentOrigin,
  PaymentType
} from '../../types/app-types'
import Alert from '../common/Alert'
import {faSpinner} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import LoaderInline from '../common/LoaderInline'
import numeral from 'numeral'
import PaymentMethodsResponse from '@adyen/adyen-web/dist/types/core/ProcessResponse/PaymentMethodsResponse/PaymentMethodsResponse'

interface ApplicationFeeCardProps {
  leaseApplication: LeaseApplication
  onBack: (e: any) => void
  onNext: (e: any) => void
  onUpdate: (updates: Record<string, any>) => void
  isPaymentTimeout: boolean
}

const ApplicationFeeCard = ({
  leaseApplication,
  onBack,
  onNext,
  onUpdate,
  isPaymentTimeout
}: ApplicationFeeCardProps) => {
  const propertyId = leaseApplication?.leasePropertyId
  const applicationFee = leaseApplication?.applicationFee
    ? parseNumber(leaseApplication?.applicationFee)
    : 0
  const hasAdminFee =
    leaseApplication?.applicationType === LeaseApplicationType.Primary &&
    isNumeric(leaseApplication?.adminFee)
  const adminFee = hasAdminFee ? parseNumber(leaseApplication?.adminFee) : 0
  const totalFees = numeral(applicationFee).add(adminFee).value()
  const totalFeesInCents = numeral(totalFees).multiply(100).value()

  const isPaymentSubmitted = hasApplicationStatus(
    leaseApplication?.applicationStatus!,
    LeaseApplicationStatus.PaymentInProgress
  )
  const isPaymentProcessed = hasApplicationStatus(
    leaseApplication?.applicationStatus!,
    LeaseApplicationStatus.PaymentCompleted
  )
  const isPaymentFailed =
    leaseApplication?.applicationStatus === LeaseApplicationStatus.PaymentFailed

  const paymentMethodOptions = [
    {
      id: 'creditCard',
      label: 'Credit/Debit Card',
      paymentType: PaymentType.OneTimePaymentCC
    },
    {
      id: 'achDirectDebit',
      label: 'ACH Direct Debit',
      paymentType: PaymentType.OneTimePaymentACH
    }
  ]
  const [paymentMethod, selectPaymentMethod] = useState(paymentMethodOptions[0])
  const paymentMethodsQuery = useGetPaymentMethodsQuery(propertyId, {
    skip: !leaseApplication
  })
  const allowedPaymentMethods = paymentMethodsQuery.data
    ? filterAllowedPaymentMethods(
        paymentMethod.paymentType,
        paymentMethodsQuery.data
      )
    : ({paymentMethods: []} as PaymentMethodsResponse)
  const paymentSessionQuery = useGetPaymentSessionQuery(
    {
      leaseApplicationId: leaseApplication?.id,
      amountInCents: totalFeesInCents,
      reservationId: 'N/A - LEASE APPLICATION',
      propertyId,
      paymentAccount: false,
      paymentOrigin: PaymentOrigin.LeaseApplication,
      paymentType: paymentMethod.paymentType,
      returnUrl: window.location.href
    },
    {skip: !leaseApplication}
  )

  const isLoading =
    paymentMethodsQuery.isLoading || paymentSessionQuery.isLoading

  return (
    <div className="card-default w-full">
      <div className="card-body-default">
        <div className="flex-grow">
          <h1 className="card-header">Lease Application Fee</h1>

          {/* Status Messages */}
          {isPaymentSubmitted && !isPaymentProcessed && !isPaymentTimeout ? (
            <Alert type="info" className="mb-2">
              <p>
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="text-lg loader-spinner mr-2"
                />
                Please wait while your lease application and payment process.
                This may take several minutes.
              </p>
            </Alert>
          ) : (
            ''
          )}

          {isPaymentProcessed ? (
            <Alert type="success" className="mb-2">
              <p>
                Your lease application and payment have successfully processed.
                <br />
                <a className="link" onClick={onNext}>
                  Please continue with your lease application.
                </a>
              </p>
            </Alert>
          ) : (
            ''
          )}

          {isPaymentFailed || isPaymentTimeout ? (
            <Alert type="error" className="mb-8">
              <p>
                An error occurred while processing your lease application
                payment. Please try again.
                <br />
                If problem persists, please contact support: hello@placemakr.com
              </p>
            </Alert>
          ) : (
            ''
          )}

          {/* Loader */}
          {!isPaymentSubmitted && isLoading ? (
            <LoaderInline text="Loading payment form..." />
          ) : (
            ''
          )}

          {/* Payment Form */}
          {!isPaymentSubmitted && !isLoading ? (
            <>
              {hasAdminFee ? (
                <p className="ml-5">
                  A one-time application fee of
                  <span className="font-bold mx-1">
                    {formatAmount(applicationFee)}
                  </span>
                  and an administrative fee of
                  <span className="font-bold mx-1">
                    {formatAmount(adminFee)}
                  </span>
                  are required at the time of applying for the lease. The
                  application fee is charged per applicant, covering the cost of
                  background and credit checks incurred by Placemakr, and is
                  non-refundable. The administrative fee, required per apartment
                  or lease, covers administrative costs associated with move-in
                  and is only refundable if your application is rejected.
                  Acceptable payment methods include credit card, debit card,
                  and direct debit (ACH).
                </p>
              ) : (
                <p className="ml-5">
                  A one-time application fee of
                  <span className="font-bold mx-1">
                    {formatAmount(applicationFee)}
                  </span>
                  is required at the time of applying for the lease. The
                  application fee is charged per applicant, covering the cost of
                  background and credit checks incurred by Placemakr, and is
                  non-refundable. Acceptable payment methods include credit
                  card, debit card, and direct debit (ACH).
                </p>
              )}

              <div className="form-control py-4">
                <label className="label label-text">Payment Method</label>
                <select
                  className="select select-bordered select-primary"
                  onChange={e => {
                    const selection =
                      paymentMethodOptions[e.target.selectedIndex]
                    selectPaymentMethod(selection)
                  }}
                >
                  {paymentMethodOptions.map(({id, label}) => (
                    <option key={id}>{label}</option>
                  ))}
                </select>
              </div>
              <AdyenPaymentForm
                paymentMethods={allowedPaymentMethods}
                paymentSession={paymentSessionQuery.data}
                isSavedPayment={false}
                onPaymentCompleted={() => {
                  onUpdate({
                    applicationStatus: LeaseApplicationStatus.PaymentInProgress
                  })
                }}
              />
            </>
          ) : (
            ''
          )}
        </div>
        <div className="card-actions flex-none mt-8 flex flex-row justify-end">
          <button className="btn btn-ghost" onClick={onBack}>
            Back
          </button>
          <button
            className={classNames('btn', {
              'btn-primary': isPaymentProcessed,
              'btn-disabled': !isPaymentProcessed
            })}
            onClick={onNext}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  )
}

export default ApplicationFeeCard
